export type LatLng = { lat: number; lng: number };

export type NormalizeLatLngResult =
  | { ok: true; lat: number; lng: number; swapped: boolean }
  | { ok: false; reason: string };

export function normalizeLatLng(
  lat: unknown,
  lng: unknown,
  opts?: { allowZeroZero?: boolean; nearZeroEpsilon?: number }
): NormalizeLatLngResult {
  const allowZeroZero = opts?.allowZeroZero ?? false;
  const eps = opts?.nearZeroEpsilon ?? 1e-4; // ~11m latitude; treats "almost zero" as invalid.

  if (typeof lat !== "number" || typeof lng !== "number") {
    return { ok: false, reason: "not_numbers" };
  }
  if (!Number.isFinite(lat) || !Number.isFinite(lng)) {
    return { ok: false, reason: "not_finite" };
  }

  const isZeroZero = lat === 0 && lng === 0;
  const isNearZero = Math.abs(lat) <= eps && Math.abs(lng) <= eps;
  if (isZeroZero && !allowZeroZero) {
    return { ok: false, reason: "zero_zero" };
  }
  if (isNearZero && !allowZeroZero) {
    return { ok: false, reason: "near_zero" };
  }

  const within = (v: number, maxAbs: number) => Math.abs(v) <= maxAbs;
  const okNormal = within(lat, 90) && within(lng, 180);
  if (okNormal) return { ok: true, lat, lng, swapped: false };

  // Obvious swap: latitude looks like longitude (<= 180) and longitude looks like latitude (<= 90)
  const okSwapped = within(lat, 180) && within(lng, 90);
  if (okSwapped) return { ok: true, lat: lng, lng: lat, swapped: true };

  return { ok: false, reason: "out_of_range" };
}

export function normalizeLngLatTuple(
  coord: unknown
): { ok: true; coord: [number, number]; swapped: boolean } | { ok: false } {
  if (!Array.isArray(coord) || coord.length < 2) return { ok: false };
  const x = coord[0];
  const y = coord[1];
  if (typeof x !== "number" || typeof y !== "number") return { ok: false };
  if (!Number.isFinite(x) || !Number.isFinite(y)) return { ok: false };

  // Interpret input as [lng,lat] unless it clearly looks like [lat,lng].
  // Clear swap pattern: first value fits latitude range, second value fits longitude range but not latitude range.
  const absX = Math.abs(x);
  const absY = Math.abs(y);
  const looksLikeLatLng = absX <= 90 && absY > 90 && absY <= 180;
  const swapped = looksLikeLatLng;
  const lng = swapped ? y : x;
  const lat = swapped ? x : y;

  if (Math.abs(lat) > 90 || Math.abs(lng) > 180) return { ok: false };
  if (lat === 0 && lng === 0) return { ok: false };
  if (Math.abs(lat) <= 1e-4 && Math.abs(lng) <= 1e-4) return { ok: false };

  return { ok: true, coord: [lng, lat], swapped };
}

